import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { FeatureFlagRequests, featureFlagRequestKey } from './feature-flag-requests';

export type ManagedFeatureFlagApp = 'customer-app' | 'team-admin' | 'backend';
export type ManagedFeatureFlagRuleEffect = 'enable' | 'disable';

export interface ManagedFeatureFlagRule {
  id: string;
  effect: ManagedFeatureFlagRuleEffect;
  teamIds: string[];
  excludeTeamIds: string[];
  companyIds: string[];
  excludeCompanyIds: string[];
  partnerIds: string[];
  excludePartnerIds: string[];
  states: string[];
  excludeStates: string[];
}

export interface ManagedFeatureFlagSection {
  id: string;
  name: string;
  position: number;
}

export interface ManagedFeatureFlag {
  id: string;
  sectionId?: string | null;
  key: string;
  name: string;
  description: string | null;
  appTargets: ManagedFeatureFlagApp[];
  isActive: boolean;
  defaultEnabled: boolean;
  userOverrides: {
    includeUserIds: string[];
    excludeUserIds: string[];
  };
  rules: ManagedFeatureFlagRule[];
  schemaVersion: number;
  createdAt: string;
  modifiedAt: string;
  createdByUserId?: string | null;
  modifiedByUserId?: string | null;
}

export interface ManagedFeatureFlagPayload {
  key: string;
  name: string;
  description?: string | null;
  appTargets: ManagedFeatureFlagApp[];
  isActive: boolean;
  defaultEnabled: boolean;
  userOverrides: {
    includeUserIds: string[];
    excludeUserIds: string[];
  };
  rules: ManagedFeatureFlagRule[];
  schemaVersion: number;
}

export type ManagedFeatureFlagComparisonStatus =
  | 'same'
  | 'changed'
  | 'only-in-qa'
  | 'only-in-production';

export interface ManagedFeatureFlagSnapshot {
  key: string;
  name: string;
  description: string | null;
  appTargets: ManagedFeatureFlagApp[];
  isActive: boolean;
  defaultEnabled: boolean;
  userOverrides: {
    includeUserIds: string[];
    excludeUserIds: string[];
  };
  rules: Array<Omit<ManagedFeatureFlagRule, 'id'>>;
  schemaVersion: 1;
}

export interface ManagedFeatureFlagEnvironmentComparisonResult {
  key: string;
  status: ManagedFeatureFlagComparisonStatus;
  differentFields: Array<
    'presence' | 'name' | 'description' | 'appTargets' | 'isActive' | 'defaultEnabled'
    | 'userOverrides' | 'rules' | 'schemaVersion'
  >;
  qa: ManagedFeatureFlagSnapshot | null;
  production: ManagedFeatureFlagSnapshot | null;
}

export interface ManagedFeatureFlagEnvironmentComparison {
  environments: {
    current: 'qa' | 'production';
    qa: 'qa';
    production: 'production';
  };
  summary: {
    total: number;
    same: number;
    changed: number;
    onlyInQa: number;
    onlyInProduction: number;
  };
  results: ManagedFeatureFlagEnvironmentComparisonResult[];
}

export interface ManagedFeatureFlagEvaluationRequest {
  app: ManagedFeatureFlagApp;
  keys?: string[];
  contextMode?: 'exact';
  context?: {
    userId?: string;
    teamId?: string;
    companyId?: string;
    partnerId?: string;
    state?: string;
  };
}

export interface ManagedFeatureFlagEvaluationResult {
  key: string;
  enabled: boolean;
  source:
    | 'inactive'
    | 'user-include'
    | 'user-exclude'
    | 'rule-enable'
    | 'rule-disable'
    | 'default-enabled'
    | 'default-disabled'
    | 'app-target-mismatch'
    | 'not-found';
  matchedRuleId?: string;
}

export interface ManagedFeatureFlagTeamOption {
  id: string;
  name: string;
}

export interface ManagedFeatureFlagCompanyOption {
  id: string;
  name: string;
  type?: string;
  recordType?: string;
}

const COMPARISON_STATUSES: ManagedFeatureFlagComparisonStatus[] = [
  'same',
  'changed',
  'only-in-qa',
  'only-in-production',
];

const COMPARISON_SUMMARY_FIELDS: Array<keyof ManagedFeatureFlagEnvironmentComparison['summary']> = [
  'total',
  'same',
  'changed',
  'onlyInQa',
  'onlyInProduction',
];

const COMPARISON_DIFFERENT_FIELDS = new Set<string>([
  'presence',
  'name',
  'description',
  'appTargets',
  'isActive',
  'defaultEnabled',
  'userOverrides',
  'rules',
  'schemaVersion',
]);

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(record: Record<string, any>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isComparisonDifferentFields(
  value: unknown,
): value is ManagedFeatureFlagEnvironmentComparisonResult['differentFields'] {
  return Array.isArray(value)
    && value.every((field) => typeof field === 'string' && COMPARISON_DIFFERENT_FIELDS.has(field));
}

function isComparisonRule(value: unknown): boolean {
  if (!isRecord(value) || (value.effect !== 'enable' && value.effect !== 'disable')) {
    return false;
  }

  return [
    'teamIds', 'excludeTeamIds', 'companyIds', 'excludeCompanyIds',
    'partnerIds', 'excludePartnerIds', 'states', 'excludeStates',
  ].every((field) => isStringArray(value[field]));
}

function isComparisonSnapshot(value: unknown): value is ManagedFeatureFlagSnapshot | null {
  if (value === null) {
    return true;
  }
  if (!isRecord(value) || typeof value.key !== 'string' || typeof value.name !== 'string') {
    return false;
  }
  if (value.description !== null && typeof value.description !== 'string') {
    return false;
  }
  if (!Array.isArray(value.appTargets)
    || !value.appTargets.every((app: unknown) => app === 'customer-app' || app === 'team-admin' || app === 'backend')) {
    return false;
  }
  if (typeof value.isActive !== 'boolean' || typeof value.defaultEnabled !== 'boolean') {
    return false;
  }
  if (!isRecord(value.userOverrides)
    || !isStringArray(value.userOverrides.includeUserIds)
    || !isStringArray(value.userOverrides.excludeUserIds)) {
    return false;
  }

  return Array.isArray(value.rules)
    && value.rules.every(isComparisonRule)
    && value.schemaVersion === 1;
}

function isEnvironmentComparison(value: unknown): value is ManagedFeatureFlagEnvironmentComparison {
  if (!isRecord(value) || !isRecord(value.environments) || !isRecord(value.summary)) {
    return false;
  }
  if ((value.environments.current !== 'qa' && value.environments.current !== 'production')
    || value.environments.qa !== 'qa'
    || value.environments.production !== 'production') {
    return false;
  }
  if (!COMPARISON_SUMMARY_FIELDS.every((field) =>
    typeof value.summary[field] === 'number' && Number.isFinite(value.summary[field]))) {
    return false;
  }
  if (!Array.isArray(value.results)) {
    return false;
  }

  return value.results.every((result: unknown) => {
    if (!isRecord(result)
      || typeof result.key !== 'string'
      || !result.key.length
      || !COMPARISON_STATUSES.includes(result.status)
      || !isComparisonDifferentFields(result.differentFields)
      || !hasOwn(result, 'qa')
      || !hasOwn(result, 'production')) {
      return false;
    }

    return isComparisonSnapshot(result.qa) && isComparisonSnapshot(result.production);
  });
}

function isEnvironmentComparisonResponse(
  response: unknown,
): response is { success: true; data: ManagedFeatureFlagEnvironmentComparison } {
  return isRecord(response)
    && response.success === true
    && isEnvironmentComparison(response.data);
}

@Injectable({
  providedIn: 'root',
})
export class ManagedFeatureFlagsService {
  private readonly requests = new FeatureFlagRequests<ManagedFeatureFlagEvaluationResult>();
  private evaluationSession?: ReturnType<typeof TabStorageService.credentials.captureIdentity>;

  constructor(private restangular: ApiClient) {}

  list(): Promise<ManagedFeatureFlag[]> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .get()
      .toPromise()
      .then((response: any) => response?.data || []);
  }

  listSections(): Promise<ManagedFeatureFlagSection[]> {
    return this.sectionsRequest('get').then((data) => {
      if (!Array.isArray(data) || data.some((section) => !section || typeof section.id !== 'string'
        || typeof section.name !== 'string' || !Number.isInteger(section.position))) {
        throw new Error('Invalid sections response.');
      }
      return data;
    });
  }

  createSection(name: string): Promise<void> { return this.sectionsRequest('post', '', { name }); }
  renameSection(section: ManagedFeatureFlagSection, name: string): Promise<void> {
    return this.sectionsRequest('put', section.id, { name, previousName: section.name });
  }
  reorderSections(sectionIds: string[], previousSectionIds: string[]): Promise<void> {
    return this.sectionsRequest('post', 'reorder', { sectionIds, previousSectionIds });
  }
  deleteSection(id: string): Promise<void> { return this.sectionsRequest('delete', id); }
  assignSection(flagIds: string[], sectionId: string | null): Promise<void> {
    return this.sectionsRequest('post', 'assign', { flagIds, sectionId });
  }

  private sectionsRequest(method: 'get' | 'post' | 'put' | 'delete', path = '', payload?: unknown): Promise<any> {
    let endpoint = this.restangular.one('v5').one('managed-feature-flags').one('sections');
    if (path) { endpoint = endpoint.one(path); }
    const request = method === 'get' ? endpoint.get()
      : method === 'post' ? endpoint.customPOST(payload)
      : method === 'put' ? endpoint.customPUT(payload) : endpoint.remove();
    return request.toPromise().then((response: any) => {
      if (response?.success !== true) { throw new Error('Section request did not succeed.'); }
      return response.data;
    });
  }

  getById(id: string): Promise<ManagedFeatureFlag> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one(id)
      .get()
      .toPromise()
      .then((response: any) => response?.data);
  }

  listCompanyOptions(): Promise<ManagedFeatureFlagCompanyOption[]> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one('company-options')
      .get()
      .toPromise()
      .then((response: any) => response?.data || []);
  }

  listTeamOptions(): Promise<ManagedFeatureFlagTeamOption[]> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one('team-options')
      .get()
      .toPromise()
      .then((response: any) => response?.data || []);
  }

  getEnvironmentComparison(): Promise<ManagedFeatureFlagEnvironmentComparison> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one('environment-comparison')
      .get()
      .toPromise()
      .then((response: any) => {
        if (!isEnvironmentComparisonResponse(response)) {
          throw new Error('Managed Feature Flag environment comparison response is malformed.');
        }

        return response.data;
      });
  }

  create(payload: ManagedFeatureFlagPayload): Promise<ManagedFeatureFlag> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .customPOST(payload)
      .toPromise()
      .then((response: any) => response?.data);
  }

  update(id: string, payload: ManagedFeatureFlagPayload): Promise<ManagedFeatureFlag> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one(id)
      .customPUT(payload)
      .toPromise()
      .then((response: any) => response?.data);
  }

  delete(id: string): Promise<void> {
    return this.restangular
      .one('v5')
      .one('managed-feature-flags')
      .one(id)
      .remove()
      .toPromise();
  }

  evaluate(
    request: ManagedFeatureFlagEvaluationRequest,
    forceRefresh = false,
  ): Promise<ManagedFeatureFlagEvaluationResult[]> {
    const session = TabStorageService.credentials.captureIdentity();
    if (!this.evaluationSession?.current()) {
      this.requests.clear();
      this.evaluationSession = session;
    }
    // Snapshot inputs before the microtask dispatch; caller mutation cannot change scope.
    const snapshot: ManagedFeatureFlagEvaluationRequest = JSON.parse(JSON.stringify(request));
    const load = async (keys?: string[]): Promise<ManagedFeatureFlagEvaluationResult[]> => {
      if (!session.current()) { throw new Error('Feature flag session changed'); }
      const response = await this.restangular.one('v5').one('managed-feature-flags').one('evaluate')
        .customPOST(keys ? {...snapshot, keys} : snapshot).toPromise();
      if (!session.current()) { throw new Error('Feature flag session changed'); }
      return response?.data || [];
    };
    // Preserve server validation and the all-flags API for unkeyed/invalid requests.
    if (!Array.isArray(snapshot.keys) || !snapshot.keys.length
      || snapshot.keys.some(key => typeof key !== 'string' || !key.trim())) {
      return load();
    }
    const keys = snapshot.keys;
    const scope = featureFlagRequestKey({app: snapshot.app, context: snapshot.context, contextMode: snapshot.contextMode});
    return Promise.all(keys.map(key => this.requests.request(scope, key, load,
      () => ({key, enabled: false, source: 'not-found'}), forceRefresh)));
  }

  async isEnabled(
    key: string,
    app: ManagedFeatureFlagApp,
    context?: ManagedFeatureFlagEvaluationRequest['context'],
    forceRefresh = false,
    contextMode?: ManagedFeatureFlagEvaluationRequest['contextMode'],
  ): Promise<boolean> {
    const results = await this.evaluate({
      app,
      keys: [key],
      context,
      contextMode,
    }, forceRefresh);

    return results.find(result => result.key === key)?.enabled === true;
  }
}
